/**
 * Article editor – X-style: status bar (Draft • Last saved), Preview + Publish,
 * cover image uploader, title, author line, TinyMCE/body editor.
 */
import {useCallback, useEffect, useMemo, useState} from 'react'
import {ScrollView, TextInput, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {useSession} from '#/state/session'
import {
  useStudioArticleQuery,
  useStudioCreateArticle,
  useStudioUpdateArticle,
} from '#/state/queries/studio'
import {useProfileQuery} from '#/state/queries/profile'
import {atoms as a, useBreakpoints, useTheme, web} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {sanitizeHandle} from '#/lib/strings/handles'
import {sanitizeDisplayName} from '#/lib/strings/display-names'
import type {CommonNavigatorParams} from '#/lib/routes/types'
import {ArticleBodyEditor} from '#/screens/Articles/ArticleBodyEditor'
import {ArticleCoverImageUploader} from '#/screens/Articles/ArticleCoverImageUploader'

const ARTICLE_EDITOR_MAX_WIDTH = 720

type Props = NativeStackScreenProps<CommonNavigatorParams, 'ArticleEditor'>

function lastSavedLabel(updatedAt: string | undefined, _: (s: string) => string): string {
  if (!updatedAt) return _(msg`Last saved just now`)
  const updated = new Date(updatedAt).getTime()
  const now = Date.now()
  const diffSec = Math.floor((now - updated) / 1000)
  if (diffSec < 60) return _(msg`Last saved just now`)
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return _(msg`Last saved ${diffMin}m ago`)
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return _(msg`Last saved ${diffHr}h ago`)
  return new Date(updatedAt).toLocaleDateString()
}

export function ArticleEditorScreen({route, navigation}: Props) {
  const {_} = useLingui()
  const t = useTheme()
  const {gtMobile} = useBreakpoints()
  const {currentAccount} = useSession()
  const articleId = route.params?.articleId

  const {data: existing} = useStudioArticleQuery(articleId ?? null)
  const createArticle = useStudioCreateArticle()
  const updateArticle = useStudioUpdateArticle()
  const {data: profile} = useProfileQuery({did: currentAccount?.did ?? ''})

  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [coverImagePath, setCoverImagePath] = useState<string | null>(null)
  const [lastSavedAt, setLastSavedAt] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (existing) {
      setTitle(existing.title)
      setContent(existing.content ?? '')
      setCoverImagePath(existing.cover_image_path ?? null)
      setLastSavedAt(existing.updated_at)
    }
  }, [existing])

  const isNew = !articleId
  const isSaving = createArticle.isPending || updateArticle.isPending
  const wordCount = useMemo(
    () => content.trim().split(/\s+/).filter(Boolean).length,
    [content],
  )
  const lastSavedText = lastSavedLabel(lastSavedAt, _)

  const onSaveDraft = useCallback(async () => {
    if (!currentAccount?.did) return
    try {
      if (isNew) {
        const art = await createArticle.mutateAsync({
          title: title || _(msg`Untitled`),
          content,
          cover_image_path: coverImagePath ?? undefined,
          owner_did: currentAccount.did,
          status: 'draft',
        })
        setLastSavedAt(new Date().toISOString())
        navigation.replace('ArticleEditor', {articleId: art.id})
      } else {
        await updateArticle.mutateAsync({
          id: articleId,
          input: {
            title: title || _(msg`Untitled`),
            content,
            cover_image_path: coverImagePath ?? undefined,
            status: 'draft',
          },
        })
        setLastSavedAt(new Date().toISOString())
        navigation.navigate('Articles')
      }
    } catch {
      // Error surfaced by mutation
    }
  }, [
    currentAccount?.did,
    isNew,
    title,
    content,
    articleId,
    createArticle,
    updateArticle,
    navigation,
    _,
  ])

  const onPublish = useCallback(async () => {
    if (!currentAccount?.did) return
    try {
      if (isNew) {
        const art = await createArticle.mutateAsync({
          title: title || _(msg`Untitled`),
          content,
          cover_image_path: coverImagePath ?? undefined,
          owner_did: currentAccount.did,
          status: 'published',
        })
        navigation.navigate('Article', {articleId: art.id})
      } else {
        await updateArticle.mutateAsync({
          id: articleId,
          input: {
            title: title || _(msg`Untitled`),
            content,
            cover_image_path: coverImagePath ?? undefined,
            status: 'published',
          },
        })
        navigation.navigate('Article', {articleId})
      }
    } catch {
      // Error surfaced by mutation
    }
  }, [
    currentAccount?.did,
    isNew,
    title,
    content,
    coverImagePath,
    articleId,
    createArticle,
    updateArticle,
    navigation,
    _,
  ])

  const onPreview = useCallback(() => {
    if (articleId) {
      navigation.navigate('Article', {articleId})
    }
  }, [articleId, navigation])

  if (!currentAccount) {
    return (
      <Layout.Screen>
        <Layout.Center>
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            <Trans>Sign in to write articles.</Trans>
          </Text>
        </Layout.Center>
      </Layout.Screen>
    )
  }

  return (
    <Layout.Screen>
      <View
        style={[
          a.flex_1,
          a.w_full,
          gtMobile && web({maxWidth: ARTICLE_EDITOR_MAX_WIDTH, alignSelf: 'center'}),
        ]}>
      {/* Status bar: Draft • Last saved | Preview | Publish | ⋮ | word count */}
      <View
        style={[
          a.flex_row,
          a.align_center,
          a.justify_between,
          a.px_lg,
          a.py_md,
          a.border_b,
          t.atoms.border_contrast_low,
          t.atoms.bg,
        ]}>
        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
          <Trans>Draft</Trans> • {lastSavedText}
        </Text>
        <View style={[a.flex_row, a.align_center, a.gap_sm]}>
          <Button
            label={_(msg`Preview`)}
            color="secondary"
            size="small"
            onPress={onPreview}
            disabled={!articleId}>
            <ButtonText>
              <Trans>Preview</Trans>
            </ButtonText>
          </Button>
          <Button
            label={_(msg`Publish`)}
            color="primary"
            size="small"
            onPress={onPublish}
            disabled={isSaving}>
            <ButtonText>
              <Trans>Publish</Trans>
            </ButtonText>
          </Button>
          <View style={[a.px_sm]} />
          <Text style={[a.text_sm, t.atoms.text_contrast_low]}>
            {wordCount} {_(msg`words`)}
          </Text>
        </View>
      </View>

      <ScrollView
        style={[a.flex_1]}
        contentContainerStyle={[a.p_lg, a.pb_2xl]}
        keyboardShouldPersistTaps="handled">
        {/* Cover image uploader (5:2 recommended, X-style) */}
        <ArticleCoverImageUploader
          coverImagePath={coverImagePath}
          onCoverChange={setCoverImagePath}
          disabled={isSaving}
        />

        {/* Title */}
        <TextInput
          value={title}
          onChangeText={setTitle}
          placeholder={_(msg`Add a title`)}
          placeholderTextColor={t.palette.contrast_400}
          style={[
            a.text_2xl,
            a.font_bold,
            a.pt_xl,
            a.pb_sm,
            t.atoms.text,
            {minHeight: 48},
          ]}
          editable={!isSaving}
        />

        {/* Author line */}
        <View style={[a.flex_row, a.align_center, a.gap_sm, a.pb_lg]}>
          <UserAvatar
            avatar={profile?.avatar}
            size={28}
            type="user"
            hideLiveBadge
          />
          <Text style={[a.text_md, a.font_semi_bold, t.atoms.text]}>
            {sanitizeDisplayName(profile?.displayName ?? currentAccount.displayName ?? '')}
          </Text>
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            @{sanitizeHandle(profile?.handle ?? currentAccount.handle ?? '', '')}
          </Text>
        </View>

        {/* Body – TinyMCE on web, TextInput on native */}
        <View style={[a.pt_md]}>
          <ArticleBodyEditor
            value={content}
            onChange={setContent}
            placeholder={_(msg`Start writing`)}
            editable={!isSaving}
          />
        </View>

        {/* Save draft (secondary action) */}
        <View style={[a.pt_xl]}>
          <Button
            label={_(msg`Save draft`)}
            color="secondary"
            size="medium"
            onPress={onSaveDraft}
            disabled={isSaving}>
            <ButtonText>
              <Trans>Save draft</Trans>
            </ButtonText>
          </Button>
        </View>
      </ScrollView>
      </View>
    </Layout.Screen>
  )
}
